import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { useApolloClient } from '@apollo/client';
import DynamicFormWidget from '@components/dynamic-form/DynamicFormWidget';
import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { normalizeDynamicFormDrawerValues } from '@components/dynamic-form/utils';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { getSystemCurrencies } from '@features/configurations/system-currency/services';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { IBaseDrawerProps } from '@common/types';
import { useDrawerSubmission } from '@common/hooks';
import {
  useGetPolicyDistributionLovs,
  useGetRelatedCompanies
} from '@common/slices';
import {
  ALL,
  exportPolicyDistributionDrawerSections,
  excelColumnWidths,
  excelFacsHeaderGroups,
  excelFacsHeaders,
  excelHeaderGroups,
  excelHeaders,
  excelTemplateStyles
} from '@common/constants';
import { IExportPolicyDistributionFilters } from '@common/types';
import { exportPolicyDistribution } from '@common/services';

import DateService from 'src/core/services/DateService';
import { downloadExcelFileMultiSheet } from '@utils/excel/download-json-multi-sheet-excel-service';

// Utility functions
const buildLovs = (companies: any, policyNumbers: any, treaties: any) => {
  const companiesList = companies?.salesforce?.getRelatedCompanies || [];
  const policyNumbersList = policyNumbers?.production?.getAllPolicies || [];
  const treatiesList = treaties?.reinsurance?.getAllTreaties || [];

  const company: Record<string, string> = { [ALL]: 'All' };
  companiesList.forEach((n: any) => {
    if (n?.code) company[String(n.id)] = String(`${n.title}`);
  });

  const policyNumber: Record<string, string> = { [ALL]: 'All' };
  policyNumbersList.forEach((n: any) => {
    if (n?.code) policyNumber[String(n.id)] = String(n.policyNumber);
  });

  const treaty: Record<string, string> = { [ALL]: 'All' };
  treatiesList.forEach((n: any) => {
    if (n?.code) treaty[String(n.id)] = String(`${n.code} - ${n.name}`);
  });

  return { company, policyNumber, treaty };
};

const normalizeMulti = (v: any): string[] => {
  if (!v) return [];
  return Array.isArray(v) ? v : [v];
};

const formatForFileName = (d: any) =>
  DateService.formatDate(d).replaceAll('/', '-');

const fetchAllProductionDistributions = async (
  client: any,
  query: any,
  vars: {
    companies: string[] | null;
    policies: string[] | null;
    treaties: string[] | null;
    effectiveDateFrom: any | null;
    effectiveDateTo: any | null;
    createdOnFrom: any | null;
    createdOnTo: any | null;
    keywordSearch: string | null;
    OrderBy: any | null;
  }
) => {
  const pageSize = 500;
  let pageNumber = 1;
  const all: any[] = [];
  const facultatives: any[] = [];
  let hasMore = true;

  while (hasMore) {
    const { data } = await client.query({
      query,
      fetchPolicy: 'no-cache',
      variables: {
        pagination: { pageSize, pageNumber },
        companies: vars.companies,
        policies: vars.policies,
        treaties: vars.treaties,
        effectiveDateFrom: vars.effectiveDateFrom,
        effectiveDateTo: vars.effectiveDateTo,
        createdOnDateFrom: vars.createdOnFrom,
        createdOnDateTo: vars.createdOnTo
      }
    });

    const items =
      data?.reinsurance?.exportPolicyDistributions?.items.treaties || [];
    const facultativesItems =
      data?.reinsurance?.exportPolicyDistributions?.items.facultatives || [];

    all.push(...items);
    facultatives.push(...facultativesItems);
    hasMore = (items.length || facultativesItems.length) === pageSize;
    pageNumber += 1;
  }

  return { treaties: all, facultatives };
};

const mapItemsToExcelData = async (data: any, client: any, query: any) => {
  const { data: systemCurrency } = await client.query({
    query,
    fetchPolicy: 'no-cache'
  });

  const treaties = (data.treaties ?? [])
    .map((p: any) => ({
      policyNumber: p.policy.policyNumber,
      planCover: p.policyPlanCover?.planCover?.cover?.name || '',
      treaty: p.treaty.name,
      treatyCurrency: p.treaty.currency.title,
      layerType: p.layerType.title,
      layerOrder: p.layerOrder,
      excessAmount: p.excessAmount,
      minimumLayerLimit: `${p.treaty.currency.symbol} ${p.minimumLayerLimitPolicyCurrency}`,
      maximumLayerLimit: `${p.treaty.currency.symbol} ${p.maximumLayerLimitPolicyCurrency}`,
      retentionlimit: `${p.treaty.currency.symbol} ${p.retentionLimitPolicyCurrency}`,
      retainedSumInsured: `${p.treaty.currency.symbol} ${p.retainedSumInsured}`,
      retainedSumInsuredBasic: `${systemCurrency.core.getSystemCurrencies.basicCurrencyIDNavigation.symbol} ${p.retainedSumInsuredBasic}`,
      retainedSumInsuredSecondary: `${systemCurrency.core.getSystemCurrencies.secondaryCurrencyIDNavigation.symbol} ${p.retainedSumInsuredSecondary}`,
      cededSumInsured: `${p.treaty.currency.symbol} ${p.cededSumInsured}`,
      cededSumInsuredBasic: `${systemCurrency.core.getSystemCurrencies.basicCurrencyIDNavigation.symbol} ${p.cededSumInsuredBasic}`,
      cededSumInsuredSecondary: `${systemCurrency.core.getSystemCurrencies.secondaryCurrencyIDNavigation.symbol} ${p.cededSumInsuredSecondary}`,
      retainedNetPremium: `${p.treaty.currency.symbol} ${p.retainedNetPremium}`,
      retainedNetPremiumBasic: `${systemCurrency.core.getSystemCurrencies.basicCurrencyIDNavigation.symbol} ${p.retainedNetPremiumBasic}`,
      retainedNetPremiumSecondary: `${systemCurrency.core.getSystemCurrencies.secondaryCurrencyIDNavigation.symbol} ${p.retainedNetPremiumSecondary}`,
      cededNetPremium: `${p.treaty.currency.symbol} ${p.cededNetPremium}`,
      cededNetPremiumBasic: `${systemCurrency.core.getSystemCurrencies.basicCurrencyIDNavigation.symbol} ${p.cededNetPremiumBasic}`,
      cededNetPremiumSecondary: `${systemCurrency.core.getSystemCurrencies.secondaryCurrencyIDNavigation.symbol} ${p.cededNetPremiumSecondary}`,
      commission: `${p.treaty.currency.symbol} ${p.comission}`,
      commissionBasic: `${systemCurrency.core.getSystemCurrencies.basicCurrencyIDNavigation.symbol} ${p.commissionBasic}`,
      commissionSecondary: `${systemCurrency.core.getSystemCurrencies.secondaryCurrencyIDNavigation.symbol} ${p.commissionSecondary}`
    }))
    .sort((a: any, b: any) =>
      String(a.code || '').localeCompare(String(b.code || ''))
    );

  const facultatives = (data?.facultatives ?? [])
    .map((p: any) => ({
      policyNumber: p.policy.policyNumber,
      planCover: p.policyPlanCover?.planCover?.cover?.name || '',
      cededSumInsured: `${p.facultative.currency.symbol} ${p.cededSumInsured}`,
      cededSumInsuredBasic: `${systemCurrency.core.getSystemCurrencies.basicCurrencyIDNavigation.symbol} ${p.cededSumInsuredBasic}`,
      cededSumInsuredSecondary: `${systemCurrency.core.getSystemCurrencies.secondaryCurrencyIDNavigation.symbol} ${p.cededSumInsuredSecondary}`,
      cededNetPremium: `${p.facultative.currency.symbol} ${p.cededNetPremium}`,
      cededNetPremiumBasic: `${systemCurrency.core.getSystemCurrencies.basicCurrencyIDNavigation.symbol} ${p.cededNetPremiumBasic}`,
      cededNetPremiumSecondary: `${systemCurrency.core.getSystemCurrencies.secondaryCurrencyIDNavigation.symbol} ${p.cededNetPremiumSecondary}`,
      commission: `${p.facultative.currency.symbol} ${p.commission}`,
      commissionBasic: `${systemCurrency.core.getSystemCurrencies.basicCurrencyIDNavigation.symbol} ${p.commissionBasic}`,
      commissionSecondary: `${systemCurrency.core.getSystemCurrencies.secondaryCurrencyIDNavigation.symbol} ${p.commissionSecondary}`
    }))
    .sort((a: any, b: any) =>
      String(a.code || '').localeCompare(String(b.code || ''))
    );

  return { treaties, facultatives };
};

const ExportPolicyDistributionDrawer: React.FC<IBaseDrawerProps> = ({ onSuccess }) => {
  const client = useApolloClient();
  const { submitButtonState, formDisabled, handleSubmission } =
    useDrawerSubmission();

  const [booted, setBooted] = useState(false);

  const [values, setValues] = useState<IExportPolicyDistributionFilters>({
    company: [ALL],
    policyNumber: [ALL],
    effectiveDate: [],
    createdOn: [],
    treaty: [ALL]
  });

  const [lovs, setLovs] = useState<Record<string, Record<string, string>>>({});
  const [sections, setSections] = useState<IDynamicFormWidgetSection[]>([]);

  const { data: companys } = useGetRelatedCompanies.useQuery();
  const { data: policyNumbers, data2: treatiesData } =
    useGetPolicyDistributionLovs();

  useEffect(() => {
    if (!companys || !policyNumbers || !treatiesData) return;

    const built = buildLovs(companys, policyNumbers, treatiesData);
    setLovs(built);

    const initialSections = exportPolicyDistributionDrawerSections(
      values,
      built
    );
    setSections(initialSections);

    setBooted(true);
  }, [companys, policyNumbers, treatiesData]);

  const submitForm = async (formValues: Record<string, any>) => {
    await handleSubmission(
      async () => {
        const [data] = normalizeDynamicFormDrawerValues(sections, formValues);

        const selectedCompanies = normalizeMulti(data.company);
        const selectedPolicies = normalizeMulti(data.policyNumber);
        const selectedTreaties = normalizeMulti(data.treaty);

        const formattedDateRange = DateService.formatDateTimeRangeBackend(
          data.effectiveDate
        );
        const formattedCreatedDateRange =
          DateService.formatDateTimeRangeBackend(data.createdOn);

        const effectiveDateFrom = formattedDateRange?.[0] || null;
        const effectiveDateTo = formattedDateRange?.[1] || null;
        const createdOnDateFrom = formattedCreatedDateRange?.[0] || null;
        const createdOnDateTo = formattedCreatedDateRange?.[1] || null;

        const companiesVar = selectedCompanies.includes(ALL)
          ? null
          : selectedCompanies;

        const policiesVar = selectedPolicies.includes(ALL)
          ? null
          : selectedPolicies;

        const treatiesVar = selectedTreaties.includes(ALL)
          ? null
          : selectedTreaties;

        const pickerFrom = data.createdOn?.[0];
        const pickerTo = data.createdOn?.[1];

        let fileName = 'Redbridge_Production Distribution';
        if (pickerFrom && pickerTo) {
          fileName += `_${formatForFileName(pickerFrom)}_${formatForFileName(
            pickerTo
          )}`;
        }

        // This will need to be updated to use the slice when we create it
        const productionDistribution = await fetchAllProductionDistributions(
          client,
          exportPolicyDistribution, // Updated to use actual query
          {
            companies: companiesVar,
            policies: policiesVar,
            treaties: treatiesVar,
            effectiveDateFrom,
            effectiveDateTo,
            createdOnFrom: createdOnDateFrom,
            createdOnTo: createdOnDateTo,
            keywordSearch: null,
            OrderBy: null
          }
        );

        if (
          !(
            productionDistribution.facultatives.length ||
            productionDistribution.treaties.length
          )
        ) {
          toast.error(
            <ToastErrorMessage>
              No production distributions found for the selected filters
            </ToastErrorMessage>
          );
          return false;
        }

        const excelData = await mapItemsToExcelData(
          productionDistribution,
          client,
          getSystemCurrencies
        );

        downloadExcelFileMultiSheet({
          filename: fileName,
          sheets: [
            {
              data: excelData.treaties,
              headers: excelHeaders,
              sheetName: 'Treaty Distributions',
              groupHeaders: excelHeaderGroups,
              styles: excelTemplateStyles,
              columnWidths: excelColumnWidths,
              freezeHeaderRows: 0
            },
            {
              data: excelData.facultatives,
              headers: excelFacsHeaders,
              sheetName: 'Facultative Distributions',
              groupHeaders: excelFacsHeaderGroups,
              styles: excelTemplateStyles,
              columnWidths: excelColumnWidths,
              freezeHeaderRows: 0
            }
          ]
        });

        toast.success(
          <ToastSuccessMessage>
            Production distribution successfully exported
          </ToastSuccessMessage>
        );

        return true;
      },
      (result) => {
        if (result) onSuccess?.();
      },
      (error) => {
        console.error(error);
        toast.error(
          <ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>
        );
      }
    );
  };

  const handleChange = (
    fieldName: keyof IExportPolicyDistributionFilters,
    value: any,
    sectionIndex: number,
    updatedValues: IExportPolicyDistributionFilters
  ) => {
    const newValues = { ...updatedValues, [fieldName]: value };
    setValues(newValues);
    setSections(exportPolicyDistributionDrawerSections(newValues, lovs));
  };

  if (!booted) return <></>;

  return (
    <DynamicFormWidget
      onChange={handleChange}
      sections={sections}
      disableForm={formDisabled}
      submitButtonState={submitButtonState}
      onSubmit={submitForm}
    />
  );
};

export { ExportPolicyDistributionDrawer };
